import { create } from 'zustand';

type ModalType = 'alert' | 'confirm' | 'prompt';

export type PromptField = {
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  validator?: (value: string) => string | null;
};

type ModalState = {
  type: ModalType;
  message: string;
  noTitle: boolean;
  resolve?: (value: boolean | string[]) => void;
  isOpen: boolean;
  confirmText?: string;
  cancelText?: string;
  inputs: PromptField[];
  inputValues: string[];
  inputErrors: string[];
  open: (type: ModalType, message: string, noTitle: boolean, confirmText?: string, cancelText?: string, inputs?: PromptField[]) => Promise<boolean | string[]>;
  setInputValue: (index: number, value: string) => void;
  setInputError: (index: number, error: string) => void;
  close: () => void;
};

export const useModalStore = create<ModalState>((set, get) => ({
  type: 'alert',
  message: '',
  noTitle: false,
  confirmText: '확인',
  cancelText: '취소',
  isOpen: false,
  resolve: undefined,
  inputs: [],
  inputValues: [],
  inputErrors: [],
  open: (type, message, noTitle, confirmText, cancelText, inputs = []) =>
    new Promise((resolve) => {
      // Radix Dialog/AlertDialog는 열린 레이어 바깥을 `aria-hidden` 처리(hideOthers)함, 이때 기존 포커스가 "숨겨질 영역" 안에 남아 있으면 브라우저 콘솔에 아래 경고가 뜰 수 있음
      // "Blocked aria-hidden on an element because its descendant retained focus"
      // 모달을 열기 직전에 포커스를 해제해(blur) 접근성 트리와 포커스 상태를 일치시킴
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      if (active instanceof HTMLElement) {
        active.blur();
      }

      // 이미 열린 모달이 있으면 대기 중인 Promise가 영원히 pending 되지 않도록 취소(false)로 마무리한다
      get().resolve?.(false);

      set({
        type,
        message,
        noTitle,
        isOpen: true,
        resolve,
        confirmText,
        cancelText,
        inputs,
        inputValues: inputs.map((f) => f.defaultValue ?? ''),
        inputErrors: inputs.map(() => ''),
      });
    }),
  setInputValue: (index, value) =>
    set((state) => {
      const inputValues = [...state.inputValues];
      const inputErrors = [...state.inputErrors];
      inputValues[index] = value;
      inputErrors[index] = '';
      return { inputValues, inputErrors };
    }),
  setInputError: (index, error) =>
    set((state) => {
      const inputErrors = [...state.inputErrors];
      inputErrors[index] = error;
      return { inputErrors };
    }),
  close: () => {
    set({ isOpen: false, resolve: undefined, inputs: [], inputValues: [], inputErrors: [] });
  },
}));
